import { Injectable } from '@angular/core';
import { OfflineDbService } from './offline-db.service';
import { OfflineCryptoService } from './offline-crypto.service';
import { buildContentKey, buildIndexKey } from './offline-cache-key';

// Generic decrypt-by-key read path for the downloads hub and its lesson/note reader — the
// only place saved content is ever read back now that offline access is confined to that hub
// (no more interceptor pretending arbitrary API routes still work offline). Follows the same
// shape as OfflineBootstrapCacheService.getCached<T>, generalized to an arbitrary kind/
// resourceId instead of that service's fixed resourceId:'bootstrap' — deliberately a sibling,
// not a repurposing of that service, which is explicitly scoped to app-bootstrap data only.
@Injectable({
  providedIn: 'root',
})
export class OfflineContentReaderService {
  constructor(private offlineDb: OfflineDbService, private offlineCrypto: OfflineCryptoService) {}

  async read<T>(school: string, user: number, kind: string, resourceId: string): Promise<T | null> {
    // Same fail-closed entitlement + per-module coverage gate the deleted interceptor's
    // tryReadCache used to apply — an expired/absent entitlement, or a module that's dropped
    // out of one since it was issued (unenrollment/refund/expulsion), must refuse to serve
    // regardless of what's still sitting encrypted on disk.
    const entitlement = await this.offlineDb.getEntitlement(`${school}:${user}`);
    if (!entitlement || entitlement.expiresAt < Date.now()) return null;

    const indexEntry = await this.offlineDb.getIndexEntry(buildIndexKey(school, user, kind, resourceId));
    if (!indexEntry || indexEntry.status !== 'saved') return null;
    if (indexEntry.moduleId && !entitlement.moduleIds.includes(indexEntry.moduleId)) return null;

    const record = await this.offlineDb.getContent(buildContentKey(school, user, kind, resourceId, indexEntry.currentVersion));
    if (!record) return null;

    try {
      const deviceKey = await this.offlineCrypto.getOrCreateDeviceKey();
      const plaintext = await this.offlineCrypto.decryptBlob(deviceKey, record.iv, record.ciphertext);
      return JSON.parse(new TextDecoder().decode(plaintext)) as T;
    } catch (err) {
      console.error(`[offline] failed to decrypt cached content (${kind}:${resourceId})`, err);
      return null;
    }
  }
}
